'use client';

import Link from '@eliadmin/core/src/components/link';

import type { TemplateSettings } from '@eliadmin/core/src/api/general/types/template-settings-types';
import type {
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';
import type { TranslateFn } from '@eliadmin/core/src/hooks/use-translations';

import { fetchMenu } from '@eliadmin/core/src/api/general/general-api';
import { buildFilterQuery } from '@eliadmin/core/src/api/utils';
import { HeaderLogo } from '@eliadmin/core/src/components/header/header-logo';
import { HeaderMenu } from '@eliadmin/core/src/components/header/header-menu';
import { MobileSearchProvider } from '@eliadmin/core/src/components/header/mobile-search';
import MobileSidebar from '@eliadmin/core/src/components/header/mobile-sidebar';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { getServerState } from '@eliadmin/core/src/utils/server-state';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Button } from '../ui/button';
import { LuArrowUpRight } from 'react-icons/lu';
import { PriceOfferButton } from '../price-offer/price-offer-button';
import { ResponsiveDialogProvider } from '@eliadmin/core/src/components/ui/responsive-dialog';
import { useEffect, useState } from 'react';

export type HeaderMainProps = {
  generalLayout: GeneralLayout['data'];
  templateSettings: TemplateSettings['data'];
  className?: {
    root?: string;
    container?: string;
    toolsNavigation?: string;
    logo?: string;
    mobileMenuButton?: string;
    searchIcon?: string;
    search?: string;
    headerCategoriesButton?: string;
    headerMenu?: string;
    headerToolIcons?: string;
    profile?: string;
    contact?: string;
    cart?: string;
    socialIcons?: string;
    customHeader?: string;
    language?: string;
  };
  menu: Menu[];
  headerMenu: Menu[];
};

export function HeaderMain({ templateSettings, generalLayout, menu, headerMenu }: HeaderMainProps) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <MobileSearchProvider>
      <div
        className={cn(
          'fixed left-0 top-0 z-[40] w-full bg-background transition-all',
          'ea-header-main',
          'custom-section',
          scrolled ? 'ea-header-main__scrolled py-4' : 'py-5 md:py-6',
        )}
        style={{
          order:
            templateSettings.header?.tools_navigation?.order ||
            templateSettings.realty_header?.tools_navigation?.order,
        }}
      >
        <div
          className={cn(
            'custom-container',
            'hidden items-center justify-between md:flex',
            'ea-header-main__desktop-container',
          )}
        >
          <div className="flex items-center justify-start gap-4 xl:gap-6">
            <HeaderLogo generalLayout={generalLayout} />
            <Separator orientation="vertical" className="hidden h-8 w-[1px] bg-black/15 lg:block" />
            <Button asChild variant={'underlined'} size={'custom'} className="hidden lg:flex">
              <Link
                prefetchOnHover
                prefetch={false}
                href={`tel:${generalLayout.contact?.phone}`}
                target="_blank"
              >
                <div className="h-2 w-2 rounded-full bg-primary" /> {generalLayout.contact?.phone}{' '}
                <LuArrowUpRight size={20} />
              </Link>
            </Button>
          </div>
          <div className="flex items-center justify-end gap-3">
            <HeaderMenu menu={headerMenu} />
            <Button asChild variant={'dark'}>
              <Link prefetchOnHover prefetch={false} href={'/cennik'}>
                Cenová ponuka
              </Link>
            </Button>
            {/* <ResponsiveDialogProvider>
              <PriceOfferButton title="Cenová ponuka" variant={'dark'} />
            </ResponsiveDialogProvider> */}
          </div>
        </div>
        <div
          className={cn(
            'flex w-full items-center justify-between md:hidden',
            'ea-header-main__mobile-container',
          )}
        >
          <div className="flex items-center justify-start gap-3">
            <MobileSidebar side="left" menu={menu[0] && menu[0]?.fields} />
            <HeaderLogo generalLayout={generalLayout} scrolled={scrolled} />
          </div>
          <Button asChild variant={'dark'}>
            <Link prefetchOnHover prefetch={false} href={'/cennik'}>
              Cenová ponuka
            </Link>
          </Button>
          {/* <ResponsiveDialogProvider>
            <PriceOfferButton title="Cenová ponuka" variant={'dark'} className="px-3 py-4" />
          </ResponsiveDialogProvider> */}
        </div>
      </div>
    </MobileSearchProvider>
  );
}
